import Checkbox from '@atlaskit/checkbox';
import { Box, xcss } from '@atlaskit/primitives';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { DatabaseFieldType } from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import React, { type FunctionComponent, useCallback } from 'react';

import { getExperimentValue } from '@canvas/client-feature-flags/experiments';

import { type EditComponentProps } from '../edit/types';

import { type ViewComponentProps } from './types';

type CheckboxValueViewProps = ViewComponentProps<DatabaseFieldType.CHECKBOX> & {
  // Provided when rendered as the `edit` component (bulk-edit, filter, default value).
  onChange?: EditComponentProps<DatabaseFieldType.CHECKBOX>['onChange'];
};

const checkboxWrapperStyles = xcss({
  display: 'inline-flex'
});

const CheckboxValueView: FunctionComponent<CheckboxValueViewProps> = ({
  field,
  contentId,
  value,
  entryId,
  onChange,
  isReadonly
}) => {
  const dispatch = defaultStoreUtils.useDispatch();
  const isCheckboxEnabled =
    !isReadonly &&
    getExperimentValue('checkboxFieldType', 'enabled', {
      fireExperimentExposure: false
    });

  const toggleCheckbox = useCallback(() => {
    const nextValue = !value;

    if (onChange) {
      onChange(nextValue, {
        type: DatabaseFieldType.CHECKBOX,
        value: nextValue
      });
      return;
    }

    if (!entryId) {
      return;
    }

    dispatch(
      addChangeToDraft(contentId, {
        type: DatabaseChangeType.SET_VALUE,
        fieldId: field.id,
        entryId,
        value: {
          type: DatabaseFieldType.CHECKBOX,
          value: nextValue
        },
        change: {
          type: DatabaseFieldType.CHECKBOX,
          value: nextValue
        }
      })
    );
  }, [contentId, dispatch, entryId, field.id, onChange, value]);

  return (
    <Box xcss={checkboxWrapperStyles} data-databases-checkbox-value="">
      <Checkbox
        testId="databases-checkbox"
        isChecked={value}
        onChange={toggleCheckbox}
        isDisabled={!isCheckboxEnabled}
        size="large"
        style={{ cursor: 'pointer' }}
      />
    </Box>
  );
};

export default CheckboxValueView;
